import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { TrendChart } from "@/components/TrendChart";
import { ImpactBadge } from "@/components/Badges";
import { PageHeader } from "@/components/PageHeader";
import { Tabs, TabPanel } from "@/components/Tabs";
import { environmentLabel, wcagTargetLabel } from "@/lib/taxonomy";
import IssuesTab from "@/pages/IssuesTab";
import ChecklistTab from "@/pages/ChecklistTab";
import ReportsTab from "@/pages/ReportsTab";

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "issues", label: "Issues" },
  { id: "checklist", label: "Manual checklist" },
  { id: "reports", label: "Reports" },
];

export default function SiteDetail() {
  const { siteId } = useParams();
  const [summary, setSummary] = useState(null);
  const [trend, setTrend] = useState(null);
  const [tab, setTab] = useState("overview");

  const load = useCallback(() => {
    api.get(`/sites/${siteId}/summary`).then((r) => setSummary(r.data)).catch(() => toast.error("Could not load site"));
    api.get(`/sites/${siteId}/trend`).then((r) => setTrend(r.data)).catch(() => {});
  }, [siteId]);
  useEffect(() => { load(); }, [load]);

  if (!summary) return <p className="text-sm text-ink-muted font-mono">Loading...</p>;
  const { site } = summary;
  // Card, impact table and criterion table are three views of one list the API
  // counted once, so all three total summary.open_total. Nothing is re-counted
  // or re-summed here.
  const impactRows = summary.open_by_impact || [];
  const criterionRows = summary.open_by_criterion || [];
  const alsoAffected = summary.also_affected_criteria || [];

  return (
    <div data-testid="site-detail-page">
      <PageHeader
        title={site.name}
        above={
          <nav aria-label="Breadcrumb" className="text-xs font-mono text-ink-muted mb-2">
            <Link to="/sites" className="hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#16324F]">Sites</Link>
            <span aria-hidden="true"> / </span>
            <span aria-current="page">{site.name}</span>
          </nav>
        }
        description={
          <>
            {site.client_name} <span aria-hidden="true">&middot;</span>{" "}
            <span className="font-mono text-xs">{site.base_url}</span>{" "}
            <span aria-hidden="true">&middot;</span>{" "}
            <span className="font-mono text-xs">{environmentLabel(site.environment)}</span>{" "}
            <span aria-hidden="true">&middot;</span>{" "}
            <span className="font-mono text-xs">{wcagTargetLabel(site.wcag_target)}</span>
          </>
        }
      >
        <Tabs tabs={TABS} active={tab} onChange={setTab} label="Site sections" idPrefix="site" />
      </PageHeader>

      <TabPanel id="overview" active={tab} idPrefix="site">
        <div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white border border-gray-200 rounded-sm p-5" data-testid="site-metric-pages">
              <p className="text-xs uppercase tracking-widest text-ink-muted">Pages audited</p>
              <p className="font-mono text-3xl mt-2">{summary.pages_audited}</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-sm p-5" data-testid="site-metric-audits">
              <p className="text-xs uppercase tracking-widest text-ink-muted">Audits run</p>
              <p className="font-mono text-3xl mt-2">{summary.audits}</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-sm p-5" data-testid="site-metric-open">
              <p className="text-xs uppercase tracking-widest text-ink-muted">{summary.unit_label || "Open issues"}</p>
              <p className="font-mono text-3xl mt-2 text-[#C62828]">{summary.open_total}</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-sm p-5" data-testid="site-metric-fixed">
              <p className="text-xs uppercase tracking-widest text-ink-muted">Fixed</p>
              <p className="font-mono text-3xl mt-2 text-[#2E7D32]">{summary.issue_status.fixed || 0}</p>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-sm p-5 mb-6">
            <h2 className="font-heading text-base font-semibold mb-4">Violations over time by impact</h2>
            <TrendChart points={trend?.points} engineChanges={trend?.engine_changes} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-white border border-gray-200 rounded-sm p-5">
              <h2 className="font-heading text-base font-semibold mb-4">Open issues by impact</h2>
              <table className="w-full text-left border-collapse text-sm">
                <caption className="sr-only">Open issues grouped by impact</caption>
                <tbody>
                  {impactRows.map(([impact, n]) => (
                    <tr key={impact} className="border-b border-gray-200 last:border-0">
                      <td className="py-2"><ImpactBadge impact={impact} /></td>
                      <td className="py-2 font-mono text-right">{n}</td>
                    </tr>
                  ))}
                  <tr className="border-t border-gray-300">
                    <td className="py-2 font-semibold">Total</td>
                    <td className="py-2 font-mono text-right font-semibold">{summary.open_total}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="bg-white border border-gray-200 rounded-sm p-5">
              <h2 className="font-heading text-base font-semibold mb-4">Open issues by WCAG success criterion</h2>
              {criterionRows.length === 0 ? (
                <p className="text-sm text-ink-muted">No open issues.</p>
              ) : (
                <>
                  <table className="w-full text-left border-collapse text-sm" data-testid="criterion-table">
                    <caption className="sr-only">Open issues grouped by WCAG success criterion</caption>
                    <thead>
                      <tr className="border-b border-gray-300">
                        <th scope="col" className="py-2 pr-4 font-heading font-semibold">Criterion</th>
                        <th scope="col" className="py-2 font-heading font-semibold text-right">{summary.unit_label || "Open issues"}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {criterionRows.map((c) => (
                        <tr key={c.criterion || "unmapped"} className="border-b border-gray-200 last:border-0">
                          <td className="py-2 pr-4"><span className="font-mono text-xs mr-2">{c.criterion}</span>{c.name}</td>
                          <td className="py-2 font-mono text-right">{c.count}</td>
                        </tr>
                      ))}
                      <tr className="border-t border-gray-300">
                        <td className="py-2 pr-4 font-semibold">Total</td>
                        <td className="py-2 font-mono text-right font-semibold">{summary.open_total}</td>
                      </tr>
                    </tbody>
                  </table>
                  <p className="mt-3 text-xs text-ink-muted">
                    Each issue is counted once, under the lowest numbered criterion it fails.
                    {alsoAffected.length > 0 && ` Also failed, by issues counted above: ${alsoAffected.map((c) => c.criterion).join(", ")}.`}
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      </TabPanel>

      <TabPanel id="issues" active={tab} idPrefix="site">
        <IssuesTab siteId={siteId} onChanged={load} />
      </TabPanel>
      <TabPanel id="checklist" active={tab} idPrefix="site">
        <ChecklistTab siteId={siteId} />
      </TabPanel>
      <TabPanel id="reports" active={tab} idPrefix="site">
        <ReportsTab
          siteId={siteId}
          clientEmail={site.client_email}
          siteName={site.name}
          clientJurisdiction={site.client_jurisdiction}
        />
      </TabPanel>
    </div>
  );
}
